import React, { memo, ReactElement } from 'react';
import { Chart as ChartLib } from '@dexma/ui-components';
import ChartData, {
    ChartAxis,
    ChartSerie,
    ChartSeriesType,
    ChartAxisType,
    SerieBorder,
    NumericChartAxis,
    Resolution,
} from 'domain/entity/widgets/ChartData';
import { WidgetType } from 'domain/entity/widget';
import Branding from 'domain/entity/branding';
import { ColorType } from 'domain/entity/widgetData';
import i18n from 'asset/translation';
import { MAIN_X_AXIS_KEY } from 'adapter/out/client/widgets/data/mappers/ChartMapper';
import { parseSeries, parseXAxis, parseYAxis } from 'adapter/in/common/components/widgets/Chart/parseChartOptions';
import { ChartPoints } from 'adapter/in/common/components/widgets/Chart/types';
import { findComparisonSerie, findNotComparisonSerie, tooltipFormatter } from 'adapter/in/common/components/widgets/Chart/tooltip';

type ChartProps = {
    height: number;
    width: number;
    isOnEdit: boolean;
    data: ChartData;
    decimalSeparator: string;
    thousandSeparator: string;
    branding: Branding;
    isLoading: boolean;
    widgetType: WidgetType;
    showLegend: boolean;
    dateFormat: string;
};

export type ChartAxisVisible = ChartAxis & { visible: boolean };

const fakeDataSeries: ChartSerie[] = [
    {
        type: ChartSeriesType.LINE,
        values: [
            {
                x: new Date(Date.parse('2021-07-11T00:00:00+00:00')),
                y: 4.703,
            },
            {
                x: new Date(Date.parse('2021-10-11T00:00:00+00:00')),
                y: 4.919,
            },
        ],
        color: {
            type: ColorType.BY_DEFAULT,
            byDefault: '#000000',
        },
        axisY: 'Energía Activa',
        axisX: MAIN_X_AXIS_KEY,
        title: 'Sample Loading Title',
        meta: {},
        stack: null,
        fillPattern: SerieBorder.SOLID,
        pointLess: false,
        resolution: Resolution.D,
    },
];

const fakeAxesX: NumericChartAxis[] = [
    {
        key: MAIN_X_AXIS_KEY,
        title: 'Sample text',
        units: 'unit',
        type: ChartAxisType.NUMERIC,
        meta: {},
    },
];
const fakeAxesY: ChartAxisVisible[] = [
    {
        key: 'key',
        title: 'Sample text',
        units: 'unit',
        type: ChartAxisType.NUMERIC,
        meta: {},
        visible: true,
    },
];

const Chart = (props: ChartProps): ReactElement => {
    const {
        height,
        width,
        isOnEdit,
        data,
        decimalSeparator,
        thousandSeparator,
        branding,
        isLoading,
        widgetType,
        showLegend,
        dateFormat,
    } = props;
    const sharedTooltip = false; // For now, we will use custom tooltip for all renderizations

    const axesX = isLoading ? fakeAxesX : data.axesX;
    const axesY = isLoading
        ? fakeAxesY
        : ([
              ...data.axesY.map((axis) => ({ ...axis, visible: true })),
              {
                  key: ChartSeriesType.TOOLTIP,
                  title: '',
                  units: '',
                  type: ChartAxisType.NUMERIC,
                  meta: {},
                  visible: false,
              },
          ] as ChartAxisVisible[]);
    const series = isLoading ? fakeDataSeries : data.series;
    const hasTooltipTitle = isLoading ? false : !data.series.find((serie) => serie.type === ChartSeriesType.SCATTER);
    const isScatterChart = isLoading
        ? false
        : data.series.filter((serie) => serie.type === ChartSeriesType.SCATTER).length > 0 &&
          data.series.filter((serie) => serie.pointLess).length > 0;

    const chartContract = {
        boost: {
            seriesThreshold: 100, // Default value to avoid rendering points when hover and tooltip
        },
        chart: {
            zoomType: isOnEdit || isLoading ? 'none' : 'xy',
            height: height - 8,
            width: width - 8,
            borderRadius: 0,
            events: {
                render(this: any) {
                    /*
                     * This is a workaround to render the legend line symbol when the serie is a line
                     * and it has color stops. The issue provokes that the icon symbol is not visible.
                     * The workaround is to modify the path of the legend line to avoid the issue.
                     * It only modifies the path of the legend line when the serie has color stops, in
                     * order to not be 100% horizontal.
                     */
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.series.forEach((serie: any) => {
                        if (serie.legendLine) {
                            const pathDef = serie.legendLine.attr('d').split(' ');
                            pathDef[5] = parseFloat(pathDef[5]) + 0.0001;
                            serie.legendLine.attr({
                                d: pathDef,
                            });
                        }
                    });
                },
            },
        },
        title: {
            text: isLoading ? 'Sample title' : data.description.title,
        },
        xAxis: axesX.find((axis) => axis.key === MAIN_X_AXIS_KEY)
            ? [parseXAxis(axesX.find((axis) => axis.key === MAIN_X_AXIS_KEY)!, series, isLoading, isOnEdit, 0)]
            : axesX.map((axisX: ChartAxis, i: number) => parseXAxis(axisX, series, isLoading, isOnEdit, i)),
        yAxis: [...axesY.map((axisY: ChartAxisVisible, i: number) => parseYAxis(axisY, isLoading, i))],
        tooltip: {
            shared: sharedTooltip,
            valueDecimals: 2,
            useHTML: true,
            headerFormat: '', // Until there is a translation, hide header
            enabled: !isLoading && !isOnEdit,
            formatter(this: ChartPoints) {
                return tooltipFormatter(
                    this,
                    sharedTooltip,
                    isScatterChart,
                    dateFormat,
                    hasTooltipTitle,
                    axesX.some((axis) => axis.type === ChartAxisType.CATEGORIZED)
                );
            },
        },
        plotOptions: {
            series: {
                states: {
                    hover: {
                        enabled: !(isLoading || isOnEdit),
                    },
                },
                marker: {
                    enabled: false,
                },
                events: {
                    mouseOut(this: any) {
                        // eslint-disable-next-line react/no-this-in-sfc
                        const notComparisonSerie = findNotComparisonSerie(this.chart.series, this.name);
                        if (notComparisonSerie) {
                            notComparisonSerie.points.forEach((point) => (point?.setState ? point.setState('') : null));
                        }
                        // eslint-disable-next-line react/no-this-in-sfc
                        const comparisonSerie = findComparisonSerie(this.chart.series, this.name);
                        if (comparisonSerie) {
                            comparisonSerie.points.forEach((point) => (point?.setState ? point.setState('') : null));
                        }
                    },
                },
            },
        },
        series: parseSeries(series, widgetType, branding, isLoading, axesX, axesY),
        legend: {
            enabled: showLegend,
        },
    };

    if (isLoading) {
        return (
            <div data-testid='single-chart-widget-loading' style={{ height, width }}>
                {' '}
            </div>
        );
    }

    return (
        <ChartLib
            data-testid='single-chart-widget'
            options={chartContract}
            thousandsSep={thousandSeparator}
            decimalPoint={decimalSeparator}
            months={[
                i18n.t('common.month.JANUARY'),
                i18n.t('common.month.FEBRUARY'),
                i18n.t('common.month.MARCH'),
                i18n.t('common.month.APRIL'),
                i18n.t('common.month.MAY'),
                i18n.t('common.month.JUNE'),
                i18n.t('common.month.JULY'),
                i18n.t('common.month.AUGUST'),
                i18n.t('common.month.SEPTEMBER'),
                i18n.t('common.month.OCTOBER'),
                i18n.t('common.month.NOVEMBER'),
                i18n.t('common.month.DECEMBER'),
            ]}
            shortMonths={[
                i18n.t('common.shortMonths.JANUARY'),
                i18n.t('common.shortMonths.FEBRUARY'),
                i18n.t('common.shortMonths.MARCH'),
                i18n.t('common.shortMonths.APRIL'),
                i18n.t('common.shortMonths.MAY'),
                i18n.t('common.shortMonths.JUNE'),
                i18n.t('common.shortMonths.JULY'),
                i18n.t('common.shortMonths.AUGUST'),
                i18n.t('common.shortMonths.SEPTEMBER'),
                i18n.t('common.shortMonths.OCTOBER'),
                i18n.t('common.shortMonths.NOVEMBER'),
                i18n.t('common.shortMonths.DECEMBER'),
            ]}
            weekdays={[
                i18n.t('common.week.SUNDAY'),
                i18n.t('common.week.MONDAY'),
                i18n.t('common.week.TUESDAY'),
                i18n.t('common.week.WEDNESDAY'),
                i18n.t('common.week.THURSDAY'),
                i18n.t('common.week.FRIDAY'),
                i18n.t('common.week.SATURDAY'),
            ]}
        />
    );
};

export default memo(Chart);
